import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import { useEffect, useState } from "react";
import { useGetCurriculumGrades } from "@/services/masterData/useGetCurriculumGrades";
import { useGetModules } from "@/services/masterData";
import { useGetSubmodulesQuery } from "@/services/masterData/curriculum/useGetSubmodulesQuery";
import { useGetActiveTlmMaster } from "@/services/masterData/tlm-master/useGetActiveTlmMaster";
import {
  ACTIVITY_TYPE,
  GRADES,
  MODULE,
  SUB_MODULE,
  TOPIC,
  TOPIC_TLM,
  YEAR,
  APPLICABLE_GRADES,
  ACTIVITY_TYPES
} from "../consts";

const useFilter = ({
  filterData,
  setFilterData,
  onCancel,
}: {
  filterData: any;
  setFilterData: React.Dispatch<React.SetStateAction<any>>;
  onCancel: () => void;
}) => {
  const isMobile = useIsMobile();
  const [selectedModuleId, setSelectedModuleId] = useState<string>("");

  // Queries
  const {
    data: gradesRes,
    isPending: isGradesLoading,
    error: gradesError,
  } = useGetCurriculumGrades();

  const {
    data: modulesRes,
    isPending: isModulesLoading,
    error: modulesError,
  } = useGetModules();

  const {
    data: submodulesRes,
    isPending: isSubmodulesLoading,
    error: submodulesError,
  } = useGetSubmodulesQuery(selectedModuleId);

  const { data: tlmRes, isPending: tlmLoading } = useGetActiveTlmMaster();

  // Options mapping
  const moduleOptions = (modulesRes?.data?.[0]?.records || []).map((m: any) => ({
    label: m?.module_name,
    value: String(m?.module_id),
  }));

  const submoduleOptions = (submodulesRes?.data?.[0]?.records || []).map(
    (s: any) => ({
      label: s?.sub_module_name,
      value: String(s?.sub_module_id),
    })
  );

  const gradeOptions = (gradesRes?.data || []).map((g: any) => ({
    label: g?.curriculum_grade,
    value: String(g?.curriculum_id),
  }));

  const activityTypeOptions = Object.values(ACTIVITY_TYPES).map((type) => ({
      label: type,
      value: type,
    }));

  const tlmTopicOptions = (tlmRes?.data || []).map((m: any) => ({
    label: m?.name,
    value: String(m?.tlmMasterId),
  }));

  // Form setup
  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [MODULE]: "",
      [SUB_MODULE]: "",
      [TOPIC]: "",
      [YEAR]: "",
      [GRADES]: [],
      [ACTIVITY_TYPE]: "",
      [TOPIC_TLM]: "",
    },
  });

  // Reset form when filterData changes
  useEffect(() => {
    if (filterData) {
      form.reset({
        [MODULE]: filterData?.[MODULE] || "",
        [SUB_MODULE]: filterData?.[SUB_MODULE] || "",
        [TOPIC]: filterData?.[TOPIC] || "",
        [YEAR]: filterData?.[YEAR] || "",
        [APPLICABLE_GRADES]: filterData?.[APPLICABLE_GRADES] || [],
        [ACTIVITY_TYPE]: filterData?.[ACTIVITY_TYPE] || "",
        [TOPIC_TLM]: filterData?.[TOPIC_TLM] || "",
      });
      if (filterData?.[MODULE]) {
        setSelectedModuleId(filterData[MODULE]);
      }
    }
  }, [filterData]);

  // Watch module changes and update submodules dynamically
  const watchedModuleId = form.watch(MODULE);
  useEffect(() => {
    if (watchedModuleId) {
      setSelectedModuleId(watchedModuleId);
      form.setValue(SUB_MODULE, ""); // reset submodule when module changes
    }
  }, [watchedModuleId, form]);

  // Handle submit
  const onSubmit = (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    setFilterData(_data);
    onCancel();
  };

  const onClear = () => {
    form.reset();
    setFilterData(null);
  };

  const requiredFields = getRequiredFields(schema);

  return {
    form,
    onSubmit,
    requiredFields,
    isMobile,
    onClear,
    moduleOptions,
    submoduleOptions,
    gradeOptions,
    isModulesLoading,
    isSubmodulesLoading,
    isGradesLoading,
    activityTypeOptions,
    tlmTopicOptions
  };
};

export default useFilter;
